import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../../utils/cn";

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helpText?: string;
  options: Array<{ value: string; label: string; className?: string }>;
  placeholder?: string;
  isSearchable?: boolean;
  searchPlaceholder?: string;
  preserveSelectedOnSearch?: boolean;
  groups?: Array<{
    heading: string;
    options: Array<{ value: string; label: string; className?: string }>;
  }>;
}

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helpText,
      options,
      placeholder,
      className,
      isSearchable = false,
      searchPlaceholder = "Search...",
      preserveSelectedOnSearch = true,
      groups,
      ...props
    },
    ref,
  ) => {
    const { disabled, ...restProps } = props;
    const [searchTerm, setSearchTerm] = useState("");
    const baseOptions = useMemo(() => {
      if (!groups || groups.length === 0) return options;
      return groups.flatMap((g) => g.options);
    }, [groups, options]);
    const filteredOptions = useMemo(() => {
      if (!isSearchable || !searchTerm.trim()) return baseOptions;
      const term = searchTerm.toLowerCase();
      return baseOptions.filter((option) =>
        option.label.toLowerCase().includes(term),
      );
    }, [isSearchable, searchTerm, baseOptions]);
    const normalizedSelectedValue = useMemo(() => {
      const { value, defaultValue } = props;
      const resolve = (
        v: string | number | readonly string[] | undefined,
      ): string | undefined => {
        if (Array.isArray(v)) return v[0];
        if (typeof v === "number") return String(v);
        if (typeof v === "string") return v;
        return undefined;
      };
      return (
        resolve(
          value as unknown as string | number | readonly string[] | undefined,
        ) ??
        resolve(
          defaultValue as unknown as
            | string
            | number
            | readonly string[]
            | undefined,
        ) ??
        ""
      );
    }, [props]);

    const [internalValue, setInternalValue] = useState<string>(
      normalizedSelectedValue,
    );

    // Ensure internal state stays in sync with form value
    useEffect(() => {
      const newValue = normalizedSelectedValue;
      if (newValue !== internalValue) {
        setInternalValue(newValue);
      }
    }, [normalizedSelectedValue, internalValue]);

    const controlledValue =
      (props.value as unknown as string | undefined) ?? undefined;
    const effectiveValue = controlledValue ?? internalValue;
    const selectedOption = useMemo(
      () => baseOptions.find((o) => String(o.value) === String(effectiveValue)),
      [baseOptions, effectiveValue],
    );
    const optionsToRender = useMemo(() => {
      if (!isSearchable) return baseOptions;
      if (!preserveSelectedOnSearch || !selectedOption) return filteredOptions;
      const exists = filteredOptions.some(
        (o) => o.value === selectedOption.value,
      );
      return exists ? filteredOptions : [selectedOption, ...filteredOptions];
    }, [
      filteredOptions,
      isSearchable,
      preserveSelectedOnSearch,
      selectedOption,
      baseOptions,
    ]);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const selectedLabel = useMemo(
      () => selectedOption?.label ?? placeholder ?? "",
      [selectedOption, placeholder],
    );
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const overlayRef = useRef<HTMLDivElement | null>(null);
    type OverlayPlacement = "top" | "bottom";
    const [overlayStyle, setOverlayStyle] = useState<{
      top: number;
      left: number;
      width: number;
      placement: OverlayPlacement;
      maxHeight: number;
    } | null>(null);
    const [optionRowHeight, setOptionRowHeight] = useState<number>(0);

    const updatePosition = React.useCallback(() => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const margin = 4;
      const availableBelow = window.innerHeight - rect.bottom;
      const availableAbove = rect.top;
      const overlayEl = overlayRef.current;
      const measuredHeight = overlayEl ? overlayEl.offsetHeight : 300;
      let placement: OverlayPlacement = "bottom";
      if (
        availableBelow < Math.min(measuredHeight, 240) &&
        availableAbove > availableBelow
      ) {
        placement = "top";
      }
      const left = Math.round(rect.left);
      const width = Math.round(rect.width);
      let top: number;
      if (placement === "bottom") {
        top = Math.round(rect.bottom + margin);
      } else {
        const desiredTop =
          rect.top -
          (overlayEl ? overlayEl.offsetHeight : measuredHeight) -
          margin;
        top = Math.round(Math.max(8, desiredTop));
      }
      const maxHeight =
        placement === "bottom"
          ? Math.max(120, availableBelow - margin)
          : Math.max(120, availableAbove - margin);
      setOverlayStyle({ top, left, width, placement, maxHeight });
    }, []);

    useEffect(() => {
      const onClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        const inContainer = containerRef.current?.contains(target);
        const inOverlay = overlayRef.current?.contains(target);
        if (!inContainer && !inOverlay) setOpen(false);
      };
      document.addEventListener("mousedown", onClickOutside);
      return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    useEffect(() => {
      if (!open) return;
      const raf = requestAnimationFrame(() => updatePosition());
      const onScroll = () => updatePosition();
      const onResize = () => updatePosition();
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", onResize);
      window.addEventListener("orientationchange", onResize);
      const vv = (
        window as unknown as {
          visualViewport?: {
            addEventListener: (t: string, l: () => void) => void;
            removeEventListener: (t: string, l: () => void) => void;
          };
        }
      ).visualViewport;
      if (vv) {
        vv.addEventListener("scroll", onResize);
        vv.addEventListener("resize", onResize);
      }
      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onResize);
        window.removeEventListener("orientationchange", onResize);
        if (vv) {
          vv.removeEventListener("scroll", onResize);
          vv.removeEventListener("resize", onResize);
        }
      };
    }, [open, updatePosition]);

    useEffect(() => {
      if (!open) return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }, [open]);

    useEffect(() => {
      if (!open) return;
      const raf = requestAnimationFrame(() => {
        const firstOption = overlayRef.current?.querySelector(
          '[data-option-row="true"]',
        ) as HTMLElement | null;
        if (firstOption) {
          setOptionRowHeight(firstOption.offsetHeight);
        }
      });
      return () => cancelAnimationFrame(raf);
    }, [open, searchTerm, optionsToRender, groups]);

    const handleSelect = (value: string) => {
      const { onChange, name } = props;
      const syntheticEvent = {
        target: { value, name },
      } as unknown as React.ChangeEvent<HTMLSelectElement>;
      if (onChange) {
        onChange(syntheticEvent);
      }
      setInternalValue(value);
      setOpen(false);
      setSearchTerm("");
    };

    return (
      <div className="w-full" ref={containerRef}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        {!isSearchable ? (
          <select
            ref={ref}
            className={cn(
              "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
              error && "border-red-300 focus:ring-red-500 focus:border-red-500",
              className,
            )}
            disabled={disabled}
            {...restProps}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {groups && groups.length > 0
              ? groups.map((group) => (
                  <optgroup key={group.heading} label={group.heading}>
                    {group.options.map((option, idx) => (
                      <option
                        key={`${group.heading}-${option.value}-${idx}`}
                        value={option.value}
                        className={option.className}
                      >
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ))
              : options.map((option, idx) => (
                  <option
                    key={`${option.value}-${idx}`}
                    value={option.value}
                    className={option.className}
                  >
                    {option.label}
                  </option>
                ))}
          </select>
        ) : (
          <>
            <select
              ref={ref}
              className="hidden"
              value={effectiveValue}
              onChange={(e) => handleSelect(e.target.value)}
              disabled={disabled}
              {...restProps}
            >
              {baseOptions.map((option, idx) => (
                <option
                  key={`${option.value}-${idx}`}
                  value={option.value}
                  className={option.className}
                >
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              ref={triggerRef}
              onClick={() => {
                if (disabled) return;
                setOpen((v) => {
                  const next = !v;
                  if (next) {
                    setTimeout(() => updatePosition(), 0);
                  }
                  return next;
                });
              }}
              className={cn(
                "w-full px-3 py-2 border border-gray-300 rounded-lg text-left flex items-center justify-between",
                "focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
                error &&
                  "border-red-300 focus:ring-red-500 focus:border-red-500",
                disabled && "bg-gray-100 text-gray-400 cursor-not-allowed",
                className,
              )}
            >
              <span className={cn(!effectiveValue && "text-gray-400")}>
                {selectedLabel}
              </span>
              <svg
                className="h-4 w-4 ml-2 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.061l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {open &&
              overlayStyle &&
              createPortal(
                <div
                  ref={overlayRef}
                  className={cn(
                    "z-50 bg-white border border-gray-200 rounded-lg shadow-lg",
                  )}
                  style={{
                    position: "fixed",
                    top: overlayStyle.top,
                    left: overlayStyle.left,
                    width: overlayStyle.width,
                  }}
                >
                  <div className="p-2 border-b">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={searchPlaceholder}
                      className={cn(
                        "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
                        error &&
                          "border-red-300 focus:ring-red-500 focus:border-red-500",
                      )}
                      autoFocus
                    />
                  </div>
                  <div
                    className="overflow-auto py-1"
                    style={{
                      maxHeight: Math.min(
                        overlayStyle.maxHeight,
                        (optionRowHeight || 40) * 5,
                      ),
                    }}
                  >
                    {optionsToRender.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No matches
                      </div>
                    ) : groups && groups.length > 0 && !searchTerm ? (
                      groups.map((group) => (
                        <div key={group.heading}>
                          <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            {group.heading}
                          </div>
                          {group.options.map((option, idx) => {
                            const isSelected =
                              String(option.value) === String(effectiveValue);
                            return (
                              <button
                                type="button"
                                key={`${group.heading}-${option.value}-${idx}`}
                                className={cn(
                                  "w-full text-left px-3 py-2 hover:bg-gray-100",
                                  isSelected && "bg-blue-50",
                                  option.className,
                                )}
                                data-option-row="true"
                                onClick={() => handleSelect(option.value)}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      ))
                    ) : (
                      optionsToRender.map((option, idx) => {
                        const isSelected =
                          String(option.value) === String(effectiveValue);
                        return (
                          <button
                            type="button"
                            key={`${option.value}-${idx}`}
                            className={cn(
                              "w-full text-left px-3 py-2 hover:bg-gray-100",
                              isSelected && "bg-blue-50",
                              option.className,
                            )}
                            data-option-row="true"
                            onClick={() => handleSelect(option.value)}
                          >
                            {option.label}
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>,
                document.body,
              )}
          </>
        )}
        {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
        {helpText && !error && (
          <p className="text-sm text-gray-500 mt-1">{helpText}</p>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
